import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Loader2, AlertCircle } from 'lucide-react';

const T = { vert: '#1e4a1a', or: '#c4a76c', bg: '#faf9f6', border: '#e8e5e0', textPrimary: '#1a1a1a', textSecondary: '#6b7280', textMuted: '#9ca3af', inputBg: '#f5f3ef' };

const AdminLoginPage = () => {
  const { login, setup, setupRequired, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const [isSetup, setIsSetup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '' });

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => { if (setupRequired) setIsSetup(true); }, [setupRequired]);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isSetup) {
        if (formData.password !== formData.confirmPassword) throw new Error('Les mots de passe ne correspondent pas');
        if (formData.password.length < 6) throw new Error('Minimum 6 caracteres requis');
        await setup(formData.email, formData.password, formData.name);
      } else {
        await login(formData.email, formData.password);
      }
      navigate('/admin');
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: T.vert }} />
      </div>
    );
  }

  const inputStyle = {
    background: T.inputBg,
    border: `1px solid ${T.border}`,
    color: T.textPrimary,
    fontFamily: "'Montserrat', sans-serif",
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: T.bg }} data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-12">
          <img
            src="/assets/logos/logo-dore.png"
            alt="One Green Dev"
            className="h-16 w-auto object-contain mx-auto mb-4"
            data-testid="admin-login-logo"
          />
          <p className="text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif", color: T.or }}>
            Administration
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-8 bg-white" style={{ border: `1px solid ${T.border}`, boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}>
          <h2 className="text-lg mb-1" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>
            {isSetup ? 'Configuration' : 'Acces'}
          </h2>
          <p className="text-[13px] mb-8" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textSecondary }}>
            {isSetup ? 'Creez votre compte administrateur.' : 'Identifiez-vous pour continuer.'}
          </p>

          {error && (
            <div className="mb-6 p-3 rounded-xl flex items-center gap-2 text-sm" data-testid="login-error"
              style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)', color: '#dc2626', fontFamily: "'Montserrat', sans-serif" }}>
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {isSetup && (
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider mb-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Nom complet</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required
                  data-testid="login-name-input" placeholder="Jean Dupont"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-colors focus:ring-2 focus:ring-emerald-200" style={inputStyle} />
              </div>
            )}
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider mb-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Email</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required
                data-testid="login-email-input" placeholder="admin@onegreendev.com"
                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-colors focus:ring-2 focus:ring-emerald-200" style={inputStyle} />
            </div>
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider mb-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Mot de passe</label>
              <input type="password" name="password" value={formData.password} onChange={handleChange} required
                data-testid="login-password-input" placeholder="Votre mot de passe"
                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-colors focus:ring-2 focus:ring-emerald-200" style={inputStyle} />
            </div>
            {isSetup && (
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider mb-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Confirmer le mot de passe</label>
                <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required
                  data-testid="login-confirm-password-input" placeholder="Confirmer"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-colors focus:ring-2 focus:ring-emerald-200" style={inputStyle} />
              </div>
            )}
            <button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full py-3.5 rounded-xl text-[13px] font-semibold transition-all duration-200 mt-2 text-white"
              style={{ background: loading ? 'rgba(30,74,26,0.5)' : T.vert, fontFamily: "'Montserrat', sans-serif" }}
              onMouseEnter={e => { if (!loading) e.target.style.background = '#163d14'; }}
              onMouseLeave={e => { if (!loading) e.target.style.background = T.vert; }}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : isSetup ? 'Creer le compte' : 'Se connecter'}
            </button>
          </form>

          {!isSetup && !setupRequired && (
            <p className="text-center text-[12px] mt-5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              <button onClick={() => navigate('/ogd-access-gate/forgot-password')} className="hover:underline" data-testid="forgot-password-link" style={{ color: T.or }}>
                Mot de passe oublie ?
              </button>
            </p>
          )}

          {!setupRequired && isSetup && (
            <p className="text-center text-sm mt-6" style={{ color: T.textMuted }}>
              <button onClick={() => setIsSetup(false)} className="hover:underline" style={{ color: T.vert }}>Se connecter</button>
            </p>
          )}
        </div>

        <p className="text-center text-[11px] mt-8" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>
          One Green Dev &mdash; Espace prive
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
